/**
 * The check bar — sibling to the dice bar, and the one general check this
 * ruleset has.
 *
 * **Nothing numeric goes on the wire.** The modifiers all land on the ability
 * *score* rather than on the roll, so the server resolves every one of them:
 * this names a skill or an ability and nothing else. A client that sent a
 * total would be choosing the number it rolls under, and one that added the
 * modifiers to the d20 would invert every bonus and penalty in the system.
 *
 * The skill list is the server's for the same reason the verb bar's is: which
 * skills exist, what they cost and what each is rolled against are rules, and
 * a stale copy still renders as a confident button. Skills the book gives no
 * ability are left out — `blindFight` and the two languages are standing
 * benefits, not rolls.
 *
 * `blind` is the roller's own: hidden from the room, not from them. A roll
 * hidden from the *player* is `secret`, which the engine rolls of its own
 * accord and is not offered here — it would be a player asking not to be told
 * their own result.
 */

import { titleLabel } from '../labels.js';

export interface CheckSkill {
  readonly name: string;
  readonly family: string;
  readonly ability?: string;
}

export interface CheckBarProps {
  readonly skills: readonly CheckSkill[];
  readonly selected: string;
  readonly onSelect: (value: string) => void;
  readonly blind: boolean;
  readonly onBlind: (value: boolean) => void;
  readonly group: boolean;
  readonly onGroup: (value: boolean) => void;
  readonly onCheck: () => void;
}

/** The six, as the skill table abbreviates them. Ability names, not scores. */
export const CHECK_ABILITIES = ['Str', 'Dex', 'Con', 'Int', 'Wis', 'Cha'] as const;

/**
 * What the selection means on the wire.
 *
 * Pure and exported because the two branches are a *different payload* —
 * `ability` for one of the six, `skill` for anything else — and sending the
 * wrong field is a refusal a player reads as the button being broken.
 * `renderToStaticMarkup` cannot press a button, so this is where it can be
 * asserted.
 */
export function checkPayload(selected: string): { skill?: string; ability?: string } | undefined {
  if (selected === '') return undefined;
  return (CHECK_ABILITIES as readonly string[]).includes(selected)
    ? { ability: selected }
    : { skill: selected };
}

/** What the two boxes do, said once for the tooltip and the line that appears when one is ticked. */
export const BLIND_HELP = 'Blind: only you see the result; the rest of the table is not told.';
export const PARTY_HELP =
  'Whole party: the most skilled of you rolls once, for everybody, instead of each of you rolling.';

export function CheckBar({
  skills,
  selected,
  onSelect,
  blind,
  onBlind,
  group,
  onGroup,
  onCheck,
}: CheckBarProps) {
  const checkable = skills.filter((skill) => skill.ability !== undefined);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <select
        aria-label="What to check"
        value={selected}
        onChange={(e) => onSelect(e.target.value)}
        className="rounded-md border border-neutral-300 bg-white px-2 py-1 text-sm dark:border-neutral-700 dark:bg-neutral-900"
      >
        <option value="">check…</option>
        <optgroup label="Ability">
          {CHECK_ABILITIES.map((ability) => (
            <option key={ability} value={ability}>
              {ability}
            </option>
          ))}
        </optgroup>
        {checkable.length > 0 && (
          <optgroup label="Skill">
            {checkable.map((skill) => (
              <option key={skill.name} value={skill.name}>
                {titleLabel(skill.name)} ({skill.ability})
              </option>
            ))}
          </optgroup>
        )}
      </select>
      <button
        type="button"
        disabled={selected === ''}
        onClick={onCheck}
        className="rounded-md border border-neutral-300 px-3 py-1 text-sm disabled:opacity-40 dark:border-neutral-700"
      >
        Check
      </button>
      <label
        className="flex items-center gap-1 text-xs text-neutral-500 dark:text-neutral-400"
        title={BLIND_HELP}
      >
        <input type="checkbox" checked={blind} onChange={(e) => onBlind(e.target.checked)} />
        blind
      </label>
      {/* A group action: the server picks whoever is most proficient and rolls
          once for everybody. Nothing to choose beyond asking — naming a roller
          would be the opposite of the rule. */}
      <label
        className="flex items-center gap-1 text-xs text-neutral-500 dark:text-neutral-400"
        title={PARTY_HELP}
      >
        <input type="checkbox" checked={group} onChange={(e) => onGroup(e.target.checked)} />
        whole party
      </label>
      {/* **The words, not just a tooltip.** A tooltip needs a mouse hovering the right
          label; this appears the moment either box is ticked, so a touch screen and a
          player who never hovers still read what the box does. */}
      {(blind || group) && (
        <p className="basis-full text-xs text-neutral-500 dark:text-neutral-400">
          {[blind ? BLIND_HELP : null, group ? PARTY_HELP : null].filter(Boolean).join(' ')}
        </p>
      )}
    </div>
  );
}
